<template>
  <div class="grouped-pengecekan-table">
    <div v-if="loading && rows.length === 0" class="table-state">
      Loading...
    </div>

    <div v-else-if="requestError" class="table-state">
      Failed to load data. Please try again.
    </div>

    <div v-else-if="!loading && rows.length === 0" class="table-state">
      No Data
    </div>

    <div v-else class="grouped-table-wrapper">
      <table class="table grouped-table">
        <thead>
          <tr>
            <th class="select-column">
              <input
                ref="selectAll"
                type="checkbox"
                :checked="allLoadedSelected"
                :disabled="!hasSelectableRows"
                @change="toggleSelectAll"
              >
            </th>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="{ sortable: column.sortable }"
              @click="sortByColumn(column)"
            >
              {{ column.label }}<span v-if="sortBy === column.sortKey">{{ sortOrder === 'ASC' ? '▲' : '▼' }}</span>
            </th>
            <th class="action-column"></th>
          </tr>
        </thead>

        <tbody>
          <template v-for="row in rows">
            <tr
              :key="row.group_key"
              class="parent-row"
              :class="{ 'expanded-row': isExpanded(row) }"
            >
              <td class="select-column">
                <input
                  type="checkbox"
                  :checked="isSelected(row)"
                  :disabled="!row.can_select"
                  :title="row.selection_disabled_reason || 'Select group'"
                  @change="toggleGroup(row)"
                >
              </td>
              <td class="nopol-cell">
                <button
                  type="button"
                  class="expand-button"
                  :aria-label="isExpanded(row) ? 'Collapse group' : 'Expand group'"
                  @click="toggleExpanded(row)"
                >
                  <i :class="isExpanded(row) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                </button>
                <span>{{ row.nopol }}</span>
              </td>
              <td>{{ row.driver || '-' }}</td>
              <td>{{ row.no_to }}</td>
              <td>{{ row.total_sj }}</td>
              <td>{{ row.total_coly }}</td>
              <td>{{ row.tanggal_sj }}</td>
              <td>{{ formatQueueNumbers(row) }}</td>
              <td>
                <span :class="statusClass(row.status_sj)">{{ statusIcon(row.status_sj) }}</span>
              </td>
              <td>
                <span :class="statusClass(row.status_rute)">{{ statusIcon(row.status_rute) }}</span>
              </td>
              <td class="action-column">
                <button
                  type="button"
                  class="edit-button"
                  title="View details"
                  @click="viewDetails(row)"
                >
                  <i class="fas fa-pen"></i>
                </button>
              </td>
            </tr>

            <tr v-if="isExpanded(row)" :key="`${row.group_key}-details`" class="child-row">
              <td colspan="11">
                <div class="child-details">
                  <table class="child-table">
                    <thead>
                      <tr>
                        <th>NO</th>
                        <th>NO TO</th>
                        <th>URUTAN</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(child, index) in row.children" :key="`${row.group_key}-${child.no_to}`">
                        <td>{{ child.sequence }}</td>
                        <td>{{ child.no_to }}</td>
                        <td>{{ formatChildQueue(row, child, index) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </template>

          <tr v-if="hasMore || loading" class="loading-row">
            <td colspan="11">
              <div v-observe-visibility="loadNextPage">
                {{ loading ? 'Loading...' : 'Scroll to load more' }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ObserveVisibility } from 'vue-observe-visibility'

export default {
  name: 'PengecekanGroupedTable',
  directives: {
    ObserveVisibility
  },
  props: {
    filter: {
      type: Object,
      required: true
    },
    update: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      columns: [
        { key: 'nopol', label: 'NOPOL', sortKey: 'nopol', sortable: true },
        { key: 'driver', label: 'DRIVER', sortKey: 'driver', sortable: true },
        { key: 'no_to', label: 'NO TO', sortKey: 'no_to', sortable: true },
        { key: 'total_sj', label: 'SJ Total', sortKey: 'total_sj', sortable: true },
        { key: 'total_coly', label: 'Koli Total', sortKey: 'total_coly', sortable: true },
        { key: 'tanggal_sj', label: 'TANGGAL SJ', sortKey: 'tanggal_sj', sortable: true },
        { key: 'queue_numbers', label: 'URUTAN TRUK', sortable: false },
        { key: 'status_sj', label: 'Cek Status SJ & Unit', sortable: false },
        { key: 'status_rute', label: 'Cek Status Rute', sortable: false }
      ],
      rows: [],
      selectedGroups: {},
      expandedKey: null,
      loading: false,
      currentPage: 0,
      lastPage: -1,
      total: 0,
      pageSize: 15,
      sortBy: 'tanggal_sj',
      sortOrder: 'DESC',
      requestVersion: 0,
      requestError: null
    }
  },
  computed: {
    hasMore() {
      return this.lastPage === -1 || this.currentPage < this.lastPage
    },
    selectableRows() {
      return this.rows.filter(row => row.can_select)
    },
    hasSelectableRows() {
      return this.selectableRows.length > 0
    },
    allLoadedSelected() {
      return this.hasSelectableRows && this.selectableRows.every(row => this.isSelected(row))
    },
    isIndeterminate() {
      const selectedCount = this.selectableRows.filter(row => this.isSelected(row)).length
      return selectedCount > 0 && selectedCount < this.selectableRows.length
    },
    selectedItems() {
      return Object.keys(this.selectedGroups).reduce((items, groupKey) => {
        const group = this.selectedGroups[groupKey]
        if (!group) return items

        group.children.forEach(child => {
          items.push({
            ...child,
            nopol: group.nopol,
            driver: group.driver,
            tanggal_sj: group.tanggal_sj,
            group_key: group.group_key
          })
        })

        return items
      }, [])
    }
  },
  watch: {
    update() {
      this.reload()
    },
    isIndeterminate() {
      this.syncSelectAllState()
    },
    allLoadedSelected() {
      this.syncSelectAllState()
    }
  },
  mounted() {
    this.reload()
  },
  methods: {
    syncSelectAllState() {
      this.$nextTick(() => {
        if (this.$refs.selectAll) this.$refs.selectAll.indeterminate = this.isIndeterminate
      })
    },
    buildParams(page) {
      return {
        ...this.filter,
        grouped: 'true',
        pagination: 'true',
        page,
        limit: this.pageSize,
        by: this.sortBy,
        order: this.sortOrder
      }
    },
    async reload() {
      const requestVersion = ++this.requestVersion
      this.rows = []
      this.currentPage = 0
      this.lastPage = -1
      this.total = 0
      this.expandedKey = null
      this.loading = false
      this.clearSelection()
      await this.fetchPage(1, requestVersion)
    },
    async fetchPage(page, requestVersion = this.requestVersion) {
      if (this.loading || (page > 1 && !this.hasMore)) return

      this.loading = true
      this.requestError = null

      try {
        const response = await this.$http.get('/api/validation/cek-to', {
          params: this.buildParams(page),
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache'
          }
        })

        if (requestVersion !== this.requestVersion) return

        const responseData = response.data || {}
        const data = Array.isArray(responseData.data) ? responseData.data : []
        const meta = responseData.meta || {}

        this.rows = page === 1 ? data : this.rows.concat(data)
        this.currentPage = Number(meta.currentPage || page)
        this.lastPage = meta.lastPage === undefined
          ? (data.length < this.pageSize ? 0 : -1)
          : Number(meta.lastPage)
        this.total = Number(meta.total || 0)
        this.$emit('fetch-end', this.rows)
        this.syncSelectAllState()
      } catch (error) {
        if (requestVersion === this.requestVersion) {
          this.requestError = error
          this.$emit('error', error)
        }
      } finally {
        if (requestVersion === this.requestVersion) this.loading = false
      }
    },
    loadNextPage(isVisible) {
      if (isVisible && this.hasMore && !this.loading) {
        this.fetchPage(this.currentPage + 1)
      }
    },
    sortByColumn(column) {
      if (!column.sortable) return

      if (this.sortBy === column.sortKey) {
        this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.sortBy = column.sortKey
        this.sortOrder = 'DESC'
      }

      this.reload()
    },
    isExpanded(row) {
      return this.expandedKey === row.group_key
    },
    toggleExpanded(row) {
      this.expandedKey = this.isExpanded(row) ? null : row.group_key
    },
    isSelected(row) {
      return Boolean(this.selectedGroups[row.group_key])
    },
    toggleGroup(row) {
      if (!row.can_select) return

      if (this.isSelected(row)) {
        this.$delete(this.selectedGroups, row.group_key)
      } else {
        this.$set(this.selectedGroups, row.group_key, row)
      }

      this.emitSelection()
    },
    toggleSelectAll(event) {
      const shouldSelect = event.target.checked

      this.selectableRows.forEach(row => {
        if (shouldSelect) this.$set(this.selectedGroups, row.group_key, row)
        else this.$delete(this.selectedGroups, row.group_key)
      })

      this.emitSelection()
    },
    emitSelection() {
      this.$emit('selection-change', this.selectedItems)
      this.syncSelectAllState()
    },
    clearSelection() {
      Object.keys(this.selectedGroups).forEach(groupKey => {
        this.$delete(this.selectedGroups, groupKey)
      })
      this.emitSelection()
    },
    viewDetails(row) {
      this.$emit('view-details', row)
    },
    formatQueueNumbers(row) {
      return Array.isArray(row.queue_numbers) && row.queue_numbers.length > 0
        ? row.queue_numbers.join(', ')
        : '-'
    },
    statusIcon(status) {
      return Number(status) === 1 ? '✓' : '×'
    },
    statusClass(status) {
      return Number(status) === 1 ? 'status-icon status-approved' : 'status-icon status-pending'
    },
    formatChildQueue(row, child, index) {
      const childQueueKeys = ['urutan_truk', 'queue_number', 'no_antrian', 'urutan', 'queue']
      const childQueue = childQueueKeys.reduce((value, key) => {
        return value !== null && value !== undefined && value !== '' ? value : child[key]
      }, null)
      const fallbackQueue = Array.isArray(row.queue_numbers) ? row.queue_numbers[index] : null
      const queue = childQueue !== null && childQueue !== undefined && childQueue !== ''
        ? childQueue
        : fallbackQueue

      if (queue === null || queue === undefined || queue === '') return '-'

      const formattedQueue = String(queue)
      return formattedQueue.length === 1 ? `0${formattedQueue}` : formattedQueue
    }
  }
}
</script>

<style scoped>
.grouped-pengecekan-table {
  font: normal normal normal 12px/18px Poppins;
}

.grouped-table-wrapper {
  height: clamp(320px, calc(100vh - 300px), 700px);
  min-height: 320px;
  overflow: auto;
  scrollbar-color: #b8b8b8 transparent;
  scrollbar-width: thin;
}

.grouped-table-wrapper::-webkit-scrollbar {
  width: 12px;
  height: 10px;
}

.grouped-table-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.grouped-table-wrapper::-webkit-scrollbar-thumb {
  border: 3px solid transparent;
  border-radius: 999px;
  background-clip: padding-box;
  background-color: #b8b8b8;
}

.grouped-table-wrapper::-webkit-scrollbar-thumb:hover {
  background-color: #909090;
}

.grouped-table {
  width: 100%;
  min-width: 0;
  margin: 0;
  color: #333;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.grouped-table th,
.grouped-table td {
  height: 82px;
  padding: 15px 12px;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-top: 1px solid #f0f0f0;
}

.grouped-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 90px;
  padding: 15px 12px;
  background: #fff;
  color: #343434;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  white-space: normal;
  border-top: 0;
  border-bottom: 1px solid #eeeeee;
}

.grouped-table th:nth-child(1),
.grouped-table td:nth-child(1) {
  width: 4%;
}

.grouped-table th:nth-child(2),
.grouped-table td:nth-child(2) {
  width: 10%;
}

.grouped-table th:nth-child(3),
.grouped-table td:nth-child(3) {
  width: 10%;
}

.grouped-table th:nth-child(4),
.grouped-table td:nth-child(4) {
  width: 14%;
}

.grouped-table th:nth-child(5),
.grouped-table td:nth-child(5) {
  width: 7%;
}

.grouped-table th:nth-child(6),
.grouped-table td:nth-child(6) {
  width: 7%;
}

.grouped-table th:nth-child(7),
.grouped-table td:nth-child(7) {
  width: 10%;
}

.grouped-table th:nth-child(8),
.grouped-table td:nth-child(8) {
  width: 9%;
}

.grouped-table th:nth-child(9),
.grouped-table td:nth-child(9) {
  width: 13%;
}

.grouped-table th:nth-child(10),
.grouped-table td:nth-child(10) {
  width: 10%;
}

.grouped-table th:nth-child(11),
.grouped-table td:nth-child(11) {
  width: 6%;
}

.grouped-table th.sortable {
  cursor: pointer;
}

.grouped-table th.sortable span {
  margin-left: 4px;
  font-size: 9px;
}

.select-column {
  width: 48px;
}

.select-column input[type="checkbox"] {
  width: 17px;
  height: 17px;
  margin: 0;
  accent-color: #1b698e;
  cursor: pointer;
}

.select-column input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.action-column {
  width: 64px;
}

.grouped-table th.action-column,
.grouped-table td.action-column {
  overflow: visible;
  padding-right: 4px;
  padding-left: 4px;
  text-overflow: clip;
}

.nopol-cell {
  text-align: left !important;
}

.parent-row td:nth-child(3),
.parent-row td:nth-child(4) {
  text-align: left;
}

.expand-button,
.edit-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #333;
  cursor: pointer;
}

.expand-button {
  width: 26px;
  margin-right: 2px;
  padding: 0;
}

.expand-button i {
  font-size: 14px;
}

.edit-button {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.edit-button i {
  font-size: 14px;
}

.edit-button:hover,
.edit-button:focus {
  border-color: #c9ecfb;
  box-shadow: 0 3px 8px rgba(27, 105, 142, 0.16);
  outline: 0;
}

.parent-row.expanded-row td {
  border-bottom: 0;
}

.child-row td {
  height: auto;
  padding: 0 10px 20px;
  border-top: 0;
  background: #fff;
}

.child-details {
  width: 100%;
  max-width: 520px;
  margin-left: 54px;
  padding-top: 8px;
  box-sizing: border-box;
  border-top: 1px solid #f0f0f0;
}

.child-table {
  width: 100%;
  border-collapse: collapse;
}

.child-table th,
.child-table td {
  height: 50px;
  padding: 8px 16px;
  text-align: left;
  border: 0;
}

.child-table th:first-child,
.child-table td:first-child {
  width: 70px;
  text-align: center;
}

.child-table th:last-child,
.child-table td:last-child {
  width: 105px;
  text-align: center;
}

.child-table th {
  font-weight: 400;
}

.status-icon {
  font-size: 17px;
  font-weight: 500;
}

.status-approved {
  color: #16c784;
}

.status-pending {
  color: #ff4a4a;
}

.loading-row td,
.table-state {
  height: 90px;
  color: #9b9b9b;
  text-align: center;
}

.table-state {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
